import React from 'react';
import { css, styled } from 'styled-components';
import { shouldIgnoreProp } from '../../../utils/shouldIgnoreProp';
import { IconBookmark } from '../../icon/IconBookmark';
import { IconBookmarkFilled } from '../../icon/IconBookmarkFilled';
import { IconDislike } from '../../icon/IconDislike';
import { IconDislikeFilled } from '../../icon/IconDislikeFilled';
import { IconLike } from '../../icon/IconLike';
import { IconShare } from '../../icon/IconShare';
import { IconThumbDown } from '../../icon/IconThumbDown';
import { IconThumbDownFilled } from '../../icon/IconThumbDownFilled';
import { IconThumbUp } from '../../icon/IconThumbUp';
import { IconThumbUpFilled } from '../../icon/IconThumbUpFilled';
import { IconWebsite } from '../../icon/IconWebsite';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { DESKTOP_TEXT_WIDTH } from './constants';

interface RootParams {
    isMobile: boolean;
}

export const Root = styled(Flex)
    .withConfig({ displayName: 'PostDetailsCard' })
    .attrs<RootParams>((props) => ({
        ...props,
        position: 'relative',
        width: '100%',
        direction: props.isMobile ? 'column' : { xs: 'column', sm: 'row' },
    }))``;

interface SoundContainerParams {
    isMobile: boolean;
}

export const SoundContainer = styled(Box)
    .withConfig({ displayName: 'SoundContainer' })
    .attrs<SoundContainerParams>((props) => ({
        ...props,
        position: 'absolute',
        top: props.isMobile ? 10 : 1.5,
        zIndex: 5,
    }))``;

interface TextContainerParams {
    isMobile: boolean;
}

export const TextContainer = styled(Box)
    .withConfig({
        displayName: 'TextContainer',
        shouldForwardProp: shouldIgnoreProp('isMobile'),
    })
    .attrs<TextContainerParams>((props) => ({
        ...props,
        width: props.isMobile ? '100%' : DESKTOP_TEXT_WIDTH,
        display: props.isMobile ? 'flex' : undefined,
        backgroundColor: props.isMobile
            ? 'var(--background-secondary)'
            : 'var(--background-primary)',
    }))`
    color: var(--color-primary);

    ${(props) =>
        props.isMobile
            ? css`
                  --post-background: var(--background-secondary);
              `
            : css`
                  --post-background: var(--background-primary);
              `}
`;

interface TextContentParams {
    isCut?: boolean;
    isMobile?: boolean;
}

export const TextContent = styled(Typography)
    .withConfig({ displayName: 'TextContent' })
    .attrs<TextContentParams>((props) => ({
        ...props,
        variant: 'body2',
        weight: 'regular',
        px: 2,
    }))`
    position: relative;
    height: calc(100% - 35px);
    width: 100%;
    overflow-y: auto;
    scrollbar-color: var(--color-secondary) transparent;
    scrollbar-width: thin;
    overflow-wrap: anywhere;

    ${(props) =>
        props.isCut &&
        css`
            overflow-y: hidden;
            display: -webkit-box;
            overflow: hidden;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            text-overflow: ellipsis;
        `}
    ${(props) =>
        props.isMobile &&
        css`
            padding-top: 8px;
        `}
`;

export const TextFooter = styled(Typography)
    .withConfig({ displayName: 'TextFooter' })
    .attrs((props) => ({
        ...props,
        variant: 'caption',
        weight: 'regular',
        py: 1,
        display: 'flex',
    }))`
    color: var(--color-secondary);
    align-items: center;
    flex-wrap: wrap;
`;

export const PostTextControlsContainer = styled(Flex)
    .withConfig({ displayName: 'PostTextControlsContainer' })
    .attrs((props) => ({
        ...props,
        direction: 'row',
        spacing: 1,
    }))``;

export const PostFooterContainer = styled(Flex)
    .withConfig({ displayName: 'PostFooterContainer' })
    .attrs((props) => ({
        ...props,
        as: 'footer',
        position: 'absolute',
        left: 2,
        bottom: 2,
        direction: 'row',
        alignItems: 'center',
        zIndex: 2,
    }))`
    justify-content: flex-end;
    width: calc(100% - 80px);
`;

export const PostControlsContainer = styled(Flex)
    .withConfig({ displayName: 'PostControlsContainer' })
    .attrs((props) => ({
        ...props,
        position: 'absolute',
        zIndex: 2,
    }))``;

export const MediaContentClickBlocker = styled(Box)
    .withConfig({ displayName: 'MediaContentClickBlocker' })
    .attrs((props) => ({
        ...props,
        position: 'absolute',
        top: 0,
        left: 0,
    }))``;

export const PostProfileText = styled(Typography)
    .withConfig({ displayName: 'PostProfileText' })
    .attrs((props) => ({
        ...props,
        variant: 'caption',
        textDecoration: 'none',
        overflow: 'hidden',
    }))`
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const DEFAULT_ICON_COLOR = 'var(--color-primary)';

interface IconColorProps {
    color?: string;
}

interface IconActiveProps extends IconColorProps {
    isActive: boolean;
}

export const LikeThumbFilledIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconThumbUpFilled fontSize={24} color={color} dataTestId={DataTestId.FILLED} />
);

export const LikeThumbIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconThumbUp fontSize={24} color={color} dataTestId={DataTestId.EMPTY} />
);

export const DislikeThumbFilledIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconThumbDownFilled fontSize={24} color={color} dataTestId={DataTestId.FILLED} />
);

export const DislikeThumbIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconThumbDown fontSize={24} color={color} dataTestId={DataTestId.EMPTY} />
);

export const DislikeFilledIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconDislikeFilled
        fontSize={24}
        strokeWidth="2px"
        fill={color}
        dataTestId={DataTestId.FILLED}
    />
);

export const DislikeIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconDislike fontSize={24} strokeWidth="2px" fill={color} dataTestId={DataTestId.EMPTY} />
);

export const BookmarkFilledIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconBookmarkFilled fontSize={24} color={color} />
);

export const BookmarkIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconBookmark fontSize={24} color={color} />
);

export const FavoriteHeartIcon = ({ color = DEFAULT_ICON_COLOR, isActive }: IconActiveProps) => (
    <IconLike
        fontSize={24}
        strokeWidth="2px"
        stroke={color}
        fill={isActive ? color : 'transparent'}
        dataTestId={isActive ? DataTestId.FILLED : DataTestId.EMPTY}
    />
);

export const ShareIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconShare color={color} />
);

export const WebsiteIcon = ({ color = DEFAULT_ICON_COLOR }: IconColorProps) => (
    <IconWebsite fontSize={24} color={color} />
);
